import { useCallback, useEffect, useRef } from "react";

import amplitude from "amplitude-js";
import { debounce } from "lodash-es";
import analytics from "react-ga4";

// you can find the ga ID through the source code of any site that uses Google Analytics
const GA_MEASUREMENT_ID = "G-EC4M2KFX5W";

analytics.initialize(GA_MEASUREMENT_ID);

interface HandlerRequiredParam {
  name: string;
  description: string;
  uiElement: string;
}

interface HandlerParam extends HandlerRequiredParam {
  [key: string]: string | Date | number | number[];
}

/**
 * Custom Hook to log an analytics event. Handles sending values to event.
 * Simply provide the other parts of the `EventData` object.
 *
 * @example
 *
 * import { useHandleAnalyticsEvent } from "/utils/analytics";
 *
 * function ExampleComponent() {
 *   // Obtain the logEvent function via hook
 *   const logEvent = useHandleAnalyticsEvent();
 *
 *   // Use logEvent function with your eventData
 *  const handleLogEvent = () => logEvent({
 *    name: "Click Event",
 *    description: "Button clicked",
 *    uiElement: "Button",
 *  });
 *
 *   return <button onClick={handleLogEvent}>Click Me!</button>;
 * }
 */
export const useHandleAnalyticsEvent = () => {
  return useCallback(
    ({ name, description, uiElement, ...rest }: HandlerParam) => {
      logAnalyticsEvent({
        name,
        description,
        ui_element: uiElement,
        time_stamp: new Date(),
        ...rest,
      });
    },
    []
  );
};

/**
 * Custom Hook to log an analytics event with debouncing for input fields.
 * This prevents excessive analytics events from being fired during rapid user input.
 *
 * @param debounceTime - Debounce time in milliseconds, defaults to 500ms
 *
 * @example
 *
 * import { useDebouncedAnalyticsEvent } from "/utils/analytics";
 *
 * function FilterComponent() {
 *   // Obtain the debounced log function
 *   const logDebouncedEvent = useDebouncedAnalyticsEvent(800); // 800ms debounce
 *
 *   const handleInputChange = (value) => {
 *     // Other handling logic
 *
 *     // Log the event (will be debounced)
 *     logDebouncedEvent({
 *       name: "filter_input_changed",
 *       description: "Filter input value changed",
 *       uiElement: "Filter Input",
 *       value: value
 *     });
 *   };
 *
 *   return <input onChange={(e) => handleInputChange(e.target.value)} />;
 * }
 */
export const useDebouncedAnalyticsEvent = (debounceTime = 500) => {
  const handleAnalyticsEvent = useHandleAnalyticsEvent();

  // Create the debounced callback in a way that ESLint can properly track dependencies
  const debouncedFunction = useCallback(
    (eventData: HandlerParam) => {
      // Create a debounced function inside the callback
      const debouncedHandler = debounce((data: HandlerParam) => {
        handleAnalyticsEvent(data);
      }, debounceTime);

      // Call the debounced function with the event data
      debouncedHandler(eventData);

      // Return the cancel function so we can access it outside
      return debouncedHandler;
    },
    [handleAnalyticsEvent, debounceTime]
  );

  // Reference to store the most recent debounced handler
  const debouncedRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Wrapper function that maintains reference to the latest debounced call
  const debouncedEventHandler = useCallback(
    (eventData: HandlerParam) => {
      // Cancel any existing debounced call
      if (debouncedRef.current) {
        debouncedRef.current.cancel();
      }

      // Create and store new debounced call
      debouncedRef.current = debouncedFunction(eventData);
    },
    [debouncedFunction]
  );

  // Cleanup mechanism to cancel pending debounced events on unmount
  useEffect(() => {
    return () => {
      if (debouncedRef.current) {
        debouncedRef.current.cancel();
      }
    };
  }, []);

  return debouncedEventHandler;
};

export const logAnalyticsEvent = (event: AnalyticsEvent) => {
  analytics.event(event.name, event);
};

/** Snake case is the naming convention used in Google Analytics 4 (GA4) for events and parameters **/
interface AnalyticsEventBase {
  /** Name of the event */
  name: string;
  /** Description of the event */
  description: string;
  /** Name of the UI element that triggered the event, e.g., button, list, text box, etc. */
  ui_element: string;
}

interface AnalyticsEvent extends AnalyticsEventBase {
  [key: string]: string | Date | number;
}

interface LogEventProperties {
  name: string;
  category?: string;
  eventProperties?: {
    [key: string]: string | null;
  };
}

/**
 * @deprecated This function is deprecated and will be removed in future versions.
 * Please use the new analytics tracking method instead.
 */
export const logEvent = ({
  name,
  category = "Tours",
  eventProperties = {},
}: LogEventProperties) => {
  amplitude
    .getInstance()
    .logEvent(name, { Event_Category: category, ...eventProperties });
};
